import type {Partition} from "@common-core/web-component-runtime/runtime";

import type {
  BridgeBarConfig,
  ConfigUserLink,
  ContextType,
  Deployment,
  Language,
  SidebarVariant,
  Theme,
  UserDataEntry
} from "./BridgeBarConfig";

const VALID_DEPLOYMENTS: readonly Deployment[] = ["live", "dz"];
const VALID_PARTITIONS: readonly Partition[] = ["prod", "non-prod"];
const VALID_LANGUAGES: readonly Language[] = ["en", "es", "fr"];
const VALID_CONTEXT_TYPE: readonly ContextType[] = ["common-org-entity"];
const VALID_SIDEBAR_VARIANTS: readonly SidebarVariant[] = ["fixed", "float"];

export interface RawBridgeBarConfig {
  applicationId?: unknown;
  sessionId?: unknown;
  deployment?: unknown;
  partition?: unknown;
  language?: unknown;
  theme?: Theme;
  width?: string;
  dealerLabelHtml?: string;
  userLinks?: ConfigUserLink[];
  userData?: UserDataEntry[];
  useMockData?: boolean;
  getTracker?: () => unknown;
  contextType?: unknown;
  sidebarVariant?: unknown;
}

export function normalizeBridgeBarConfig(
  config: RawBridgeBarConfig
): BridgeBarConfig {
  const errors: string[] = [];

  if (!isNonEmptyString(config.applicationId)) {
    errors.push("applicationId is required and must be a non-empty string");
  }
  if (!isNonEmptyString(config.sessionId)) {
    errors.push("sessionId is required and must be a non-empty string");
  }

  if (errors.length > 0) {
    throw new Error(
      "Bridge Bar Configuration Error\n" +
        "=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~\n" +
        "Bridge bar render method was invoked with an invalid configuration and can not be properly initialized.\n" +
        "Please check your implementation of the bridge bar render call and verify that it provides all the required " +
        "fields and that they are the correct type.\n\nThe following error(s) were found: \n * " +
        errors.join("\n * ") +
        "\n=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~\n"
    );
  }

  const contextType = normalizeContextType(config.contextType);

  return {
    applicationId: (config.applicationId as string).trim(),
    sessionId: (config.sessionId as string).trim(),
    deployment: normalizeDeployment(config.deployment),
    partition: normalizePartition(config.partition),
    language: normalizeLanguage(config.language),
    theme: config.theme,
    width: config.width,
    dealerLabelHtml: resolveDealerLabelHtml(
      config.dealerLabelHtml,
      contextType
    ),
    userLinks: config.userLinks ?? [],
    userData: config.userData ?? [],
    useMockData: config.useMockData,
    getTracker: config.getTracker,
    contextType,
    sidebarVariant: normalizeSidebarVariant(config.sidebarVariant)
  };
}

/**
 * True when the adopter asked for both a dealer label and the org context
 * selector. Exposed so the config provider can warn about it once per element,
 * rather than on every `willUpdate()` — this function runs on each property
 * change, so warning from here would repeat on every theme toggle.
 */
export function hasDealerLabelConflict(config: RawBridgeBarConfig): boolean {
  return (
    normalizeContextType(config.contextType) !== undefined &&
    isNonEmptyString(config.dealerLabelHtml)
  );
}

/**
 * `dealerLabelHtml` and `contextType` are two ways of asking for the same thing
 * — the current dealership, named in the middle of the bar — so they are
 * mutually exclusive. Opting into the selector wins: it supersedes the static
 * label, and rendering both would duplicate the dealership name and collide in
 * the layout (the label grows leftward from the bar's edge while the selector is
 * centred over it).
 *
 * Resolving this here rather than at render time keeps the choice entirely in
 * the adopter's hands and in one place: the container simply renders whichever
 * of the two its config ends up holding.
 */
function resolveDealerLabelHtml(
  dealerLabelHtml: string | undefined,
  contextType: ContextType | undefined
): string | undefined {
  return contextType && isNonEmptyString(dealerLabelHtml)
    ? undefined
    : dealerLabelHtml;
}

function normalizeContextType(value: unknown): ContextType | undefined {
  return VALID_CONTEXT_TYPE.includes(value as ContextType)
    ? (value as ContextType)
    : undefined;
}

function normalizeSidebarVariant(value: unknown): SidebarVariant {
  const normalized = toTrimmedLower(value);
  return VALID_SIDEBAR_VARIANTS.includes(normalized as SidebarVariant)
    ? (normalized as SidebarVariant)
    : "fixed";
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizeDeployment(value: unknown): Deployment {
  const normalized = toTrimmedLower(value);
  return VALID_DEPLOYMENTS.includes(normalized as Deployment)
    ? (normalized as Deployment)
    : "live";
}

function normalizePartition(value: unknown): Partition {
  const normalized = toTrimmedLower(value);
  return VALID_PARTITIONS.includes(normalized as Partition)
    ? (normalized as Partition)
    : "prod";
}

function normalizeLanguage(value: unknown): Language | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const normalized = toTrimmedLower(value);
  return VALID_LANGUAGES.includes(normalized as Language)
    ? (normalized as Language)
    : "en";
}

function toTrimmedLower(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}
